import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate, matchPath } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Create } from './pages/Create';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LocaleProvider } from './context/LocaleContext';
import { NavBar } from './components/NavBar';
import { SetupBanner } from './components/SetupBanner';
import { CookieBanner } from './components/CookieBanner';
import { Footer } from './components/Footer';
import { Home } from './pages/Home';
import { StoreNavigation } from './components/store/StoreNavigation';
import { GlobalProtection } from './components/GlobalProtection';
import { ErrorBoundary } from './components/ErrorBoundary';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from './components/SEO';
import {
  isReservedRootSegment,
  parseAccountHandle,
  parseUsernameRouteValue,
  routePatterns,
  routes,
} from './routing/routes';
import {
  getDefaultAppOrigin,
  isAuthHostname,
  isIdentityHostname,
  isCentralAuthPath,
  isLocalAuthHostname,
} from './lib/authRouting';
import {
  STOREFRONT_CHROME_EVENT,
  type StorefrontChromeEventDetail,
  type StorefrontLayout,
} from './lib/storefrontLayoutPreference';
import { WerseeIdAuth } from './pages/WerseeIdAuth';
import { WerseeIdOAuthConsent } from './pages/WerseeIdOAuthConsent';

const lazyNamed = <T extends React.ComponentType<any>>(
  loader: () => Promise<any>,
  exportName: string,
) => React.lazy(async () => {
  const module = await loader();
  return { default: module[exportName] as T };
});

const Auth = lazyNamed(() => import('./pages/Auth'), 'Auth');
const IdentityCenter = lazyNamed(() => import('./pages/IdentityCenter'), 'IdentityCenter');
const WerseeIdCenter = lazyNamed(() => import('./pages/WerseeIdCenter'), 'WerseeIdCenter');
const ForgotPassword = lazyNamed(() => import('./pages/ForgotPassword'), 'ForgotPassword');
const UpdatePassword = lazyNamed(() => import('./pages/UpdatePassword'), 'UpdatePassword');
const AuthCallback = lazyNamed(() => import('./pages/AuthCallback'), 'AuthCallback');
const AuthHandoff = lazyNamed(() => import('./pages/AuthHandoff'), 'AuthHandoff');
const AuthHandoffBegin = lazyNamed(() => import('./pages/AuthHandoff'), 'AuthHandoffBegin');
const ConfirmEmail = lazyNamed(() => import('./pages/ConfirmEmail'), 'ConfirmEmail');
const OAuthConsent = lazyNamed(() => import('./pages/OAuthConsent'), 'OAuthConsent');
const McpPermissionsWizard = lazyNamed(() => import('./pages/McpPermissionsWizard'), 'McpPermissionsWizard');
const MailLandingPage = lazyNamed(() => import('./features/mail/landing/MailLandingPage'), 'MailLandingPage');
const ProtectedMailApp = lazyNamed(() => import('./features/mail/MailSubdomainRoutes'), 'ProtectedMailApp');
const MailSubdomainRoutes = lazyNamed(() => import('./features/mail/MailSubdomainRoutes'), 'MailSubdomainRoutes');
const PrivacyPolicy = lazyNamed(() => import('./pages/PrivacyPolicy'), 'PrivacyPolicy');
const AmbassadorProgram = lazyNamed(() => import('./pages/AmbassadorProgram'), 'AmbassadorProgram');
const SearchPage = lazyNamed(() => import('./pages/Search'), 'Search');
const MarketplaceCategory = lazyNamed(() => import('./pages/MarketplaceCategory'), 'MarketplaceCategory');
const Sell = lazyNamed(() => import('./pages/Sell'), 'Sell');
const Guidelines = lazyNamed(() => import('./pages/Guidelines'), 'Guidelines');
const Profile = lazyNamed(() => import('./pages/Profile'), 'Profile');
const Announcements = lazyNamed(() => import('./pages/Announcements'), 'Announcements');
const AnnouncementDetail = lazyNamed(() => import('./pages/AnnouncementDetail'), 'AnnouncementDetail');
const ListingDetail = lazyNamed(() => import('./pages/ListingDetail'), 'ListingDetail');
const Dashboard = lazyNamed(() => import('./pages/Dashboard'), 'Dashboard');
const SellingTips = lazyNamed(() => import('./pages/SellingTips'), 'SellingTips');
const MoveFromGumroad = lazyNamed(() => import('./pages/MoveFromGumroad'), 'MoveFromGumroad');
const MoveFromWhop = lazyNamed(() => import('./pages/MoveFromWhop'), 'MoveFromWhop');
const Blog = lazyNamed(() => import('./pages/Blog'), 'Blog');
const Checkout = lazyNamed(() => import('./pages/Checkout'), 'Checkout');
const BusinessPlanCheckout = lazyNamed(() => import('./pages/BusinessPlanCheckout'), 'BusinessPlanCheckout');
const PublicBusinessCard = lazyNamed(() => import('./pages/PublicBusinessCard'), 'PublicBusinessCard');
const AiPlanCheckout = lazyNamed(() => import('./pages/AiPlanCheckout'), 'AiPlanCheckout');
const Success = lazyNamed(() => import('./pages/Success'), 'Success');
const AccessContent = lazyNamed(() => import('./pages/AccessContent'), 'AccessContent');
const FreeConfirmation = lazyNamed(() => import('./pages/FreeConfirmation'), 'FreeConfirmation');
const CommunityView = lazyNamed(() => import('./pages/CommunityView'), 'CommunityView');
const DocumentationView = lazyNamed(() => import('./pages/DocumentationView'), 'DocumentationView');
const BuyerProtection = lazyNamed(() => import('./pages/BuyerProtection'), 'BuyerProtection');
const WarrantyRules = lazyNamed(() => import('./pages/WarrantyRules'), 'WarrantyRules');
const Support = lazyNamed(() => import('./pages/Support'), 'Support');
const HelpArticle = lazyNamed(() => import('./pages/HelpArticle'), 'HelpArticle');
const LiveChat = lazyNamed(() => import('./pages/LiveChat'), 'LiveChat');
const SetupAccount = lazyNamed(() => import('./pages/SetupAccount'), 'SetupAccount');
const MobileUpload = lazyNamed(() => import('./pages/MobileUpload'), 'MobileUpload');
const MobileStoreApp = lazyNamed(() => import('./components/mobile-store/MobileStoreApp'), 'MobileStoreApp');
const MobileLoginApproval = lazyNamed(() => import('./pages/MobileLoginApproval'), 'MobileLoginApproval');
const AffiliateJoinView = lazyNamed(() => import('./components/workspace/AffiliateJoinView'), 'AffiliateJoinView');
const Terms = lazyNamed(() => import('./pages/Terms'), 'Terms');
const Eula = lazyNamed(() => import('./pages/Eula'), 'Eula');
const QuickPayCheckout = lazyNamed(() => import('./components/public/QuickPayCheckout'), 'QuickPayCheckout');
const PayoutRecipientSetup = lazyNamed(() => import('./pages/PayoutRecipientSetup'), 'PayoutRecipientSetup');
const InvoicePublicView = lazyNamed(() => import('./components/public/InvoicePublicView'), 'InvoicePublicView');
const OldInvoicePaymentPage = lazyNamed(() => import('./components/public/InvoicePaymentPage'), 'InvoicePaymentPage');
const InvoicePaymentPage = React.lazy(() => import('./pages/pay/invoice/InvoicePaymentPage'));
const InvoicePdfViewer = React.lazy(() => import('./pages/InvoicePdfViewer'));
const PackageConfirmation = React.lazy(() => import('./pages/PackageConfirmation'));
const SubscriptionPublicView = lazyNamed(() => import('./components/public/SubscriptionPublicView'), 'SubscriptionPublicView');
const CommunitySubscriptionPayPage = lazyNamed(() => import('./pages/CommunitySubscriptionPayPage'), 'CommunitySubscriptionPayPage');
const PaymentSuccess = lazyNamed(() => import('./pages/PaymentSuccess'), 'PaymentSuccess');
const PosTerminal = lazyNamed(() => import('./pages/PosTerminal'), 'PosTerminal');
const PosCheckoutPage = lazyNamed(() => import('./pages/PosCheckoutPage'), 'PosCheckoutPage');
const PosAuth = lazyNamed(() => import('./pages/PosAuth'), 'PosAuth');
const PosNfcMobile = lazyNamed(() => import('./pages/PosNfcMobile'), 'PosNfcMobile');
const PayoutInfoPage = lazyNamed(() => import('./pages/PayoutInfo'), 'PayoutInfoPage');
const FeesAndPlansPage = lazyNamed(() => import('./pages/FeesAndPlans'), 'FeesAndPlansPage');
const StatusPage = lazyNamed(() => import('./pages/StatusPage'), 'StatusPage');
const NextGenPos = lazyNamed(() => import('./pages/features/NextGenPos'), 'NextGenPos');
const WerseePay = lazyNamed(() => import('./pages/features/WerseePay'), 'WerseePay');
const WerseePaySecurityPage = lazyNamed(() => import('./pages/public/WerseePaySecurityPage'), 'WerseePaySecurityPage');
const AccountSecurityPage = lazyNamed(() => import('./pages/public/AccountSecurityPage'), 'AccountSecurityPage');
const CommandCenter = lazyNamed(() => import('./pages/features/CommandCenter'), 'CommandCenter');
const OmniManagement = lazyNamed(() => import('./pages/features/OmniManagement'), 'OmniManagement');
const AcademyBuilder = lazyNamed(() => import('./pages/features/AcademyBuilder'), 'AcademyBuilder');
const PartnerNetwork = lazyNamed(() => import('./pages/features/PartnerNetwork'), 'PartnerNetwork');
const ZeroCostEntry = lazyNamed(() => import('./pages/features/ZeroCostEntry'), 'ZeroCostEntry');
const VentureLaunchpad = lazyNamed(() => import('./pages/features/VentureLaunchpad'), 'VentureLaunchpad');
const IntelligenceCore = lazyNamed(() => import('./pages/features/IntelligenceCore'), 'IntelligenceCore');
const FlashCheckout = lazyNamed(() => import('./pages/features/FlashCheckout'), 'FlashCheckout');
const DemoFlashCheckout = lazyNamed(() => import('./pages/demo/DemoFlashCheckout'), 'DemoFlashCheckout');
const CreatorMode = React.lazy(() => import('./pages/creators/CreatorMode'));
const PublicCreatorProfile = React.lazy(() => import('./pages/creators/PublicCreatorProfile'));
const PublicSiteIndexer = lazyNamed(() => import('./pages/PublicSiteIndexer'), 'PublicSiteIndexer');
const CreatorOnboardingPage = React.lazy(() => import('./pages/creators/CreatorOnboardingPage'));
const CreatorOverview = React.lazy(() => import('./pages/creators/CreatorOverview'));
const CreatorProfile = React.lazy(() => import('./pages/creators/CreatorProfile'));
const CreatorAnalytics = React.lazy(() => import('./pages/creators/CreatorAnalytics'));
const CreatorLinks = React.lazy(() => import('./pages/creators/CreatorLinks'));
const CreatorCampaigns = React.lazy(() => import('./pages/creators/CreatorCampaigns'));
const CreatorAudience = React.lazy(() => import('./pages/creators/CreatorAudience'));
const CreatorRevenue = React.lazy(() => import('./pages/creators/CreatorRevenue'));
const CreatorEarnings = React.lazy(() => import('./pages/creators/CreatorEarnings'));
const CreatorPayouts = React.lazy(() => import('./pages/creators/CreatorPayouts'));
const CreatorInvites = React.lazy(() => import('./pages/creators/CreatorInvites'));
const CreatorPlatforms = React.lazy(() => import('./pages/creators/CreatorPlatforms'));
const CreatorShareKit = React.lazy(() => import('./pages/creators/CreatorShareKit'));
const CreatorDocs = React.lazy(() => import('./pages/creators/CreatorDocs'));
const CreatorSettings = React.lazy(() => import('./pages/creators/CreatorSettings'));
const CreatorReferralRedirect = React.lazy(() => import('./pages/creators/CreatorReferralRedirect'));
const WerseeTreasure = React.lazy(() => import('./pages/features/WerseeTreasure'));
const WerseeInvoices = React.lazy(() => import('./pages/features/WerseeInvoices'));
const WerseeSovereign = React.lazy(() => import('./pages/features/WerseeSovereign'));
const CustomAppLP = lazyNamed(() => import('./pages/CustomAppLP'), 'CustomAppLP');
const CustomAppBuild = lazyNamed(() => import('./pages/CustomAppBuild'), 'CustomAppBuild');
const EnterpriseSolutions = lazyNamed(() => import('./pages/EnterpriseSolutions'), 'EnterpriseSolutions');
const CallBooking = lazyNamed(() => import('./pages/CallBooking'), 'CallBooking');
const BookingDashboard = lazyNamed(() => import('./components/BookingDashboard'), 'BookingDashboard');
const CallConfigWizard = lazyNamed(() => import('./components/CallConfigWizard'), 'CallConfigWizard');
const SocialLandingPage = lazyNamed(() => import('./pages/SocialLandingPage'), 'SocialLandingPage');
const InvitePage = lazyNamed(() => import('./pages/InvitePage'), 'InvitePage');
const AddFriendPage = lazyNamed(() => import('./pages/AddFriendPage'), 'AddFriendPage');
const ChatInvitePage = lazyNamed(() => import('./pages/ChatInvitePage'), 'ChatInvitePage');
const BusinessChatInvitePage = lazyNamed(() => import('./pages/BusinessChatInvitePage'), 'BusinessChatInvitePage');
const PublicDmPage = lazyNamed(() => import('./pages/PublicDmPage'), 'PublicDmPage');
const CommunityInvitePage = lazyNamed(() => import('./pages/CommunityInvitePage'), 'CommunityInvitePage');
const MessageSharePage = lazyNamed(() => import('./pages/MessageSharePage'), 'MessageSharePage');
const AdsManager = lazyNamed(() => import('./components/workspace/AdsManager'), 'AdsManager');
const BuilderPage = lazyNamed(() => import('./pages/BuilderPage'), 'BuilderPage');
const LoggedOut = lazyNamed(() => import('./pages/LoggedOut'), 'LoggedOut');
const Learn = lazyNamed(() => import('./pages/Learn'), 'Learn');
const SharedFileView = lazyNamed(() => import('./pages/SharedFileView'), 'SharedFileView');
const Jobs = lazyNamed(() => import('./pages/Jobs'), 'Jobs');
const JobApplication = lazyNamed(() => import('./pages/JobApplication'), 'JobApplication');
const SecurityVerify = lazyNamed(() => import('./pages/SecurityVerify'), 'SecurityVerify');
const AboutUs = lazyNamed(() => import('./pages/AboutUs'), 'AboutUs');
const Roadmap = lazyNamed(() => import('./pages/Roadmap'), 'Roadmap');
const InteractiveExportView = lazyNamed(() => import('./pages/InteractiveExportView'), 'InteractiveExportView');
const FunnelShareView = lazyNamed(() => import('./pages/FunnelShareView'), 'FunnelShareView');
const ProposalPublicView = lazyNamed(() => import('./pages/ProposalPublicView'), 'ProposalPublicView');
const ContractPublicView = lazyNamed(() => import('./pages/ContractPublicView'), 'ContractPublicView');
const CookiePolicy = lazyNamed(() => import('./pages/CookiePolicy'), 'CookiePolicy');
const Imprint = lazyNamed(() => import('./pages/Imprint'), 'Imprint');
const Disclaimer = lazyNamed(() => import('./pages/Disclaimer'), 'Disclaimer');
const NotFound = lazyNamed(() => import('./pages/NotFound'), 'NotFound');
const InvestmentDetails = React.lazy(() => import('./pages/InvestmentDetails'));
const Portfolio = lazyNamed(() => import('./pages/Portfolio'), 'Portfolio');
const SchoolLandingPage = lazyNamed(() => import('./pages/SchoolLandingPage'), 'SchoolLandingPage');
const SchoolJoinPage = lazyNamed(() => import('./pages/SchoolJoinPage'), 'SchoolJoinPage');
const StudentDiscount = lazyNamed(() => import('./components/education/StudentDiscount'), 'StudentDiscount');
const CampusProgram = lazyNamed(() => import('./components/education/CampusProgram'), 'CampusProgram');
const LearningPaths = lazyNamed(() => import('./components/education/LearningPaths'), 'LearningPaths');
const EduResources = lazyNamed(() => import('./components/education/EduResources'), 'EduResources');
const GiveawayLandingPage = lazyNamed(() => import('./components/workspace/GiveawayLandingPage'), 'GiveawayLandingPage');
const FormPublicView = lazyNamed(() => import('./pages/FormPublicView'), 'FormPublicView');
const Pricing = lazyNamed(() => import('./components/Pricing'), 'Pricing');
const AdminPlans = lazyNamed(() => import('./components/AdminPlans'), 'AdminPlans');
const CityCampaign = lazyNamed(() => import('./pages/CityCampaign'), 'CityCampaign');
const NextGenSetup = lazyNamed(() => import('./pages/NextGenSetup'), 'NextGenSetup');
const NextGenInvite = lazyNamed(() => import('./pages/NextGenInvite'), 'NextGenInvite');
const NextGenLanding = lazyNamed(() => import('./pages/NextGenLanding'), 'NextGenLanding');
const AllPages = lazyNamed(() => import('./pages/AllPages'), 'AllPages');
const EduGames = lazyNamed(() => import('./pages/EduGames'), 'EduGames');
const GuardianPortal = lazyNamed(() => import('./pages/GuardianPortal'), 'GuardianPortal');
const SafetyControls = lazyNamed(() => import('./pages/SafetyControls'), 'SafetyControls');
const BotGuide = lazyNamed(() => import('./pages/BotGuide'), 'BotGuide');
const LinkAccount = lazyNamed(() => import('./pages/LinkAccount'), 'LinkAccount');
const WerseeBuddyLanding = lazyNamed(() => import('./pages/info/WerseeBuddyLanding'), 'WerseeBuddyLanding');

const PlaceholderPage = ({ title }: { title: string }) => (
  <>
    <SEO title={title} noIndex />
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
      <h1 className="text-2xl font-bold text-[#1D1D1F] mb-2">{title}</h1>
      <p className="text-[#86868B]">This page is still under development.</p>
    </div>
  </>
);

const SettingsRedirect = () => {
  const { section } = useParams();
  const location = useLocation();
  const normalizedSection = section ? section.toLowerCase() : 'security';
  return (
    <Navigate
      to={{
        pathname: `/workspace/settings/${normalizedSection}`,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
};

const DashboardBusinessRedirect = () => {
  const { businessId } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: businessId ? routes.workspacePage({ pageName: businessId }) : '/workspace',
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
};

const LegacyDashboardRedirect = () => {
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: '/workspace',
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
};

const LegacyChatRedirect = () => {
  const { username } = useParams();
  const location = useLocation();

  const normalizedUsername = parseUsernameRouteValue(username);

  const pathname = normalizedUsername
    ? routes.userWorkspaceChats({ username: normalizedUsername })
    : routes.workspaceChats();

  return (
    <Navigate
      to={{ pathname, search: location.search, hash: location.hash }}
      replace
    />
  );
};

const ProfileOrBusiness = lazyNamed(() => import('./components/ProfileOrBusiness'), 'ProfileOrBusiness');
const BusinessPortal = lazyNamed(() => import('./pages/BusinessPortal'), 'BusinessPortal');
const Transparency = lazyNamed(() => import('./pages/Transparency'), 'Transparency');
const DownloadApp = lazyNamed(() => import('./pages/DownloadApp'), 'DownloadApp');

const InvestMarketplace = React.lazy(() => import('./pages/invest/InvestDiscovery'));
const isInvestHostname = () => ['invest.wersee.com', 'invest.wersee.localhost'].includes(window.location.hostname.toLowerCase());
const InvestCampaignDetail = React.lazy(() => import('./pages/invest/InvestCampaignDetail'));
const InvestCheckout = React.lazy(() => import('./pages/invest/InvestCheckout'));
const BusinessInvestPage = React.lazy(() => import('./pages/invest/BusinessInvestPage'));
const AdminInvestmentsPage = React.lazy(() => import('./pages/invest/AdminInvestmentsPage'));
const MarketAssetDetail = React.lazy(() => import('./pages/invest/MarketAssetDetail'));
const WerseeListingDetail = React.lazy(() => import('./pages/invest/WerseeListingDetail'));

const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <React.Suspense fallback={<div className="min-h-screen bg-black pt-32 text-center text-gray-500">Loading...</div>}>
    {children}
  </React.Suspense>
);

const CentralAuthRoute = ({
  children,
  canonicalPath,
}: {
  children: React.ReactNode;
  canonicalPath?: string;
}) => {
  const location = useLocation();
  const shouldRedirect = !import.meta.env.DEV && !isLocalAuthHostname(window.location.hostname);

  if (!shouldRedirect) return <>{children}</>;
  return <AuthHandoffBegin authPath={canonicalPath || location.pathname} />;
};

const ReturnToWerseeApp = () => {
  const location = useLocation();
  React.useEffect(() => {
    window.location.replace(`${getDefaultAppOrigin()}${location.pathname}${location.search}${location.hash}`);
  }, [location.hash, location.pathname, location.search]);
  return <div className="min-h-[100dvh] bg-[#050505]" />;
};

const RouteErrorBoundary = ({
  disabled,
  routeKey,
  children,
}: {
  disabled: boolean;
  routeKey: string;
  children: React.ReactNode;
}) => {
  if (disabled) return <>{children}</>;

  return <ErrorBoundary key={routeKey}>{children}</ErrorBoundary>;
};

const AnimatedRoutes = () => {
  const reducedMotion = useReducedMotion();
  const location = useLocation();
  const { user } = useAuth();
  const storeCheckoutBackground = (location.state as {
    storeCheckoutBackground?: typeof location;
  } | null)?.storeCheckoutBackground;
  const contentLocation = storeCheckoutBackground || location;
  const [storefrontLayout, setStorefrontLayout] = React.useState<StorefrontLayout>('classic');

  React.useEffect(() => {
    const handleStorefrontChrome = (event: Event) => {
      const detail = (event as CustomEvent<StorefrontChromeEventDetail>).detail;
      setStorefrontLayout(detail?.layout === 'feed' ? 'feed' : 'classic');
    };
    window.addEventListener(STOREFRONT_CHROME_EVENT, handleStorefrontChrome);
    return () => window.removeEventListener(STOREFRONT_CHROME_EVENT, handleStorefrontChrome);
  }, []);

  React.useEffect(() => {
    setStorefrontLayout('classic');
  }, [location.pathname]);

  const isLoginRoute = isCentralAuthPath(location.pathname);
  const accountProductMatch = matchPath(
    routePatterns.accountProductBySlug,
    location.pathname,
  );
  const accountProductEditorMatch = matchPath(
    routePatterns.accountProductEditor,
    location.pathname,
  );
  const isValidProductOwnerSegment = (value?: string) => Boolean(
    parseAccountHandle(value)
    || (!isReservedRootSegment(value) && parseUsernameRouteValue(value)),
  );
  const isAccountProductDetail = Boolean(
    isValidProductOwnerSegment(accountProductMatch?.params.accountHandle),
  );
  const isAccountProductEditor = Boolean(
    isValidProductOwnerSegment(accountProductEditorMatch?.params.accountHandle),
  );
  const isDetail = Boolean(
    matchPath(routePatterns.productById, location.pathname)
    || matchPath(routePatterns.productBySlug, location.pathname)
    || isAccountProductDetail
    || isAccountProductEditor,
  );
  const isAuth = location.pathname.startsWith('/auth');
  const isAuthCallback = location.pathname === '/auth/callback';
  const isOAuthConsent = location.pathname.startsWith('/oauth/');
  const isWorkspace = location.pathname.includes('/workspace') || location.pathname.startsWith('/dashboard');
  const isChat = location.pathname.startsWith('/chat');
  const isAddFriend = location.pathname.startsWith('/add/');
  const isMobileUpload = location.pathname.startsWith('/upload-mobile') || location.pathname === '/scan/listing-upload';
  const isMobileStore = location.pathname === '/mobile' || location.pathname.startsWith('/mobile/');
  const isMobileLoginApproval = location.pathname === '/mobile-login/approve';
  const isCheckout = location.pathname.startsWith('/checkout');
  const isBusinessPlanCheckout =
    location.pathname.startsWith('/business/') &&
    location.pathname.includes('/subscribe/');
  const isBusinessCard = location.pathname === '/bcard' || location.pathname.startsWith('/bcard/');
  const isSuccess = location.pathname.startsWith('/success');
  const isAccess = location.pathname.startsWith('/access');
  const isFree = location.pathname.startsWith('/free-confirm');
  const isCommunityHost = ['community.wersee.com', 'community.wersee.localhost'].includes(window.location.hostname.toLowerCase());
  const routeKey = isMobileStore
    ? '/mobile'
    : contentLocation.pathname.includes('/workspace')
      ? '/workspace'
      : isLoginRoute
        ? '/auth'
        : contentLocation.pathname;
  const isCommunity = isCommunityHost || location.pathname === '/community' || location.pathname.startsWith('/community/');
  const isBuyerProtection = location.pathname.startsWith('/buyer-protection');
  const isWarrantyRules = location.pathname.startsWith('/warranty-rules');
  const isSupport = location.pathname.startsWith('/support');
  const isHelp = location.pathname.startsWith('/help/');
  const isLiveChat = location.pathname.startsWith('/live-chat');
  const isAffiliateJoin = location.pathname.startsWith('/affiliate/join');
  const isQuickPay = location.pathname.includes('/quick-pay/');
  const isInvoice = location.pathname.includes('/invoice/');
  const isSubscription = location.pathname.includes('/subscriptions/');
  const isPos = location.pathname.includes('/pos/');
  const isPaymentSuccess = location.pathname === '/payment-success';
  const isFeature = location.pathname.startsWith('/features/');
  const isFeesPlans = location.pathname === '/fees-plans';
  const isPayoutInfo = location.pathname === '/payout-info';
  const isStatus = location.pathname === '/status';
  const isCustomApp = location.pathname.startsWith('/app/');
  const isCustomAppBuild = location.pathname === '/custom-app-build';
  const isEnterprise = location.pathname === '/enterprise';
  const isPortal = location.pathname.startsWith('/portal/');
  const isLearn = location.pathname.startsWith('/learn');
  const isLearnPlayer = location.pathname.split('/').length > 2 && location.pathname.startsWith('/learn');
  const isCreators = location.pathname === '/creators' || location.pathname.startsWith('/creators/');
  const isCreatorPublic = location.pathname.startsWith('/creator/');
  const isCreatorReferral = location.pathname.startsWith('/r/');
  const isTreasure = location.pathname === '/treasure';
  const isInvoices = location.pathname === '/invoices';
  const isJobs = location.pathname === '/jobs';
  const isWerseePaySecurity = location.pathname === '/wersee-pay-security';
  const isAccountSecurity = location.pathname === '/account-security';
  const isSecurityVerify = location.pathname === '/verify';
  const isInteractiveExport = location.pathname.startsWith('/export/interactive/');
  const isProposal = location.pathname.startsWith('/proposal/');
  const isContract = location.pathname.startsWith('/contract/');
  const isCookiePolicy = location.pathname === '/cookies';
  const isImprint = location.pathname === '/imprint';
  const isDisclaimer = location.pathname === '/disclaimer';
  const isInvitePage = location.pathname.startsWith('/invite/');
  const isCommunityInvitePage = location.pathname.startsWith('/join/') || location.pathname.startsWith('/c/');
  const isGiveaway = location.pathname.startsWith('/g/');
  const isForm = location.pathname.startsWith('/f/');
  const isBuilder = location.pathname.startsWith('/builder/');
  const isCreate = location.pathname === '/create' || location.pathname.startsWith('/create/');
  const isSocialLanding = location.pathname.startsWith('/welcome/');
  const isPublicDm =
    location.pathname.startsWith('/pd/') ||
    location.pathname.startsWith('/public-dm/');
  const isPublicSiteIndexer = location.pathname === '/index-your-site';
  const isMailExperience = location.pathname === '/email' || location.pathname === '/mail';
  const isSovereign = location.pathname === '/sovereign';
  const isTransparency = location.pathname === '/transparency';
  const isBlog = location.pathname === '/blog' || location.pathname.startsWith('/blog/');
  const isBuddy = location.pathname === '/info/buddy' || location.pathname === '/buddy';
  const isInvest = location.pathname === '/invest' || location.pathname.startsWith('/invest/') || location.pathname === '/investments' || location.pathname.startsWith('/investments/') || location.pathname.startsWith('/fund/') || /^\/[^/]+\/invest(\/|$)/.test(location.pathname);

  const isNextGenWizard = location.pathname.startsWith('/next-gen-setup') || location.pathname.startsWith('/next-gen-invite');
  const isSchool = location.pathname === '/school' || location.pathname.startsWith('/school/') || location.pathname === '/for-school';

  const isEmbed = location.search.includes('embed=true');
  const hideNavAndFooter = storefrontLayout === 'feed' || isDetail || isAuth || isAuthCallback || isOAuthConsent || isWorkspace || isChat || isAddFriend || isMobileUpload || isMobileStore || isMobileLoginApproval || isCheckout || isBusinessPlanCheckout || isBusinessCard || isSuccess || isAccess || isFree || isCommunity || isWarrantyRules || isLiveChat || isAffiliateJoin || isQuickPay || isInvoice || isSubscription || isPos || isPaymentSuccess || isFeature || isCustomApp || isCustomAppBuild || isEnterprise || isPortal || isLearnPlayer || isCreators || isCreatorPublic || isCreatorReferral || isTreasure || isInvoices || isJobs || isWerseePaySecurity || isAccountSecurity || isSecurityVerify || isInteractiveExport || isProposal || isContract || isInvest || isGiveaway || isInvitePage || isCommunityInvitePage || isForm || isBuilder || isCreate || isSocialLanding || isPublicDm || isPublicSiteIndexer || isMailExperience || isBlog || isBuddy || isEmbed || isNextGenWizard || isSchool;
  
  const hideNavBar = (hideNavAndFooter || isSovereign) && !(location.pathname === '/create' && !user);
  // Dedicated applications and payment flows retain their own navigation.
  const useStoreNavigation = Boolean(user) && !(isWorkspace || isLoginRoute || isAuth || isOAuthConsent || isEmbed || isMobileStore || isMobileUpload || isMobileLoginApproval || (isCheckout && !storeCheckoutBackground) || isBusinessPlanCheckout || isQuickPay || isInvoice || isSubscription || isPos || isPaymentSuccess || isPortal || isMailExperience || isChat || isAccess || isLearnPlayer || isBuilder || isNextGenWizard || isSchool);

  return (
    <div className={`flex min-h-[100dvh] flex-col bg-[#f6f6f7] transition-colors duration-200 dark:bg-[#070707] ${useStoreNavigation ? 'ws-store-frame' : ''}`}>
      {useStoreNavigation ? <StoreNavigation /> : !hideNavBar && <NavBar />}
      
      <main className={`relative flex flex-1 flex-col bg-[#f6f6f7] dark:bg-[#070707] ${isMobileStore || isMobileLoginApproval ? '' : 'pb-safe pt-safe'} ${!hideNavAndFooter ? "pb-0" : ""}`}>
        <RouteErrorBoundary disabled={isLoginRoute} routeKey={routeKey}>
          <AnimatePresence mode="popLayout" initial={false}>
            <React.Suspense fallback={<div className="min-h-[50vh] bg-black pt-32 text-center text-gray-400" role="status">Loading page…</div>}>
            <motion.div key={routeKey} initial={useStoreNavigation && !reducedMotion ? { opacity: 0, y: 8 } : false} animate={{ opacity: 1, y: 0 }} transition={{ duration: .28 }} style={{ flex: 1, minWidth: 0 }}><Routes location={contentLocation}>
              <Route path="/" element={isCommunityHost ? <CommunityView /> : <Home />} />
              <Route path="/mobile" element={<MobileStoreApp />} />
              <Route path="/mobile/*" element={<MobileStoreApp />} />
              <Route path="/mobile-login/approve" element={<MobileLoginApproval />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/human-made" element={<MarketplaceCategory />} />
              <Route path="/digital-products" element={<MarketplaceCategory />} />
              <Route path="/services" element={<MarketplaceCategory />} />
              <Route path="/courses" element={<MarketplaceCategory />} />
              <Route path="/communities" element={<MarketplaceCategory />} />
              <Route path="/sell" element={<Sell />} />
              <Route path="/move-from-gumroad" element={<MoveFromGumroad />} />
              <Route path="/move-from-whop" element={<MoveFromWhop />} />
              <Route path="/buyer-protection" element={<BuyerProtection />} />
              <Route path="/support" element={<Support />} />
              <Route path="/help" element={<Navigate to="/support" replace />} />
              <Route path="/fees-plans" element={<FeesAndPlansPage />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/guidelines" element={<Guidelines />} />
              <Route path="/announcements" element={<Announcements />} />
              <Route path="/announcements/:id" element={<AnnouncementDetail />} />
              <Route path="/index-your-site" element={<PublicSiteIndexer />} />
              <Route path="/learn" element={<Learn />} />
              <Route path="/messages" element={<LegacyChatRedirect />} />
              <Route path={routePatterns.userProfile} element={<Profile />} />
              <Route path="/:slugOrUsername/profile" element={<Profile />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/profile/:id" element={<Profile />} />
              <Route path="/:username/chat" element={<LegacyChatRedirect />} />
              <Route path="/:username/chats" element={<LegacyChatRedirect />} />
              <Route path="/:username/messages" element={<LegacyChatRedirect />} />
              <Route path="/:username/chat/invite/:token" element={<ChatInvitePage />} />
              <Route path={routePatterns.accountProductEditor} element={<ListingDetail />} />
              <Route path={routePatterns.accountProductBySlug} element={<ListingDetail />} />
              <Route path="/share/:token" element={<SharedFileView />} />
              <Route path="/export/interactive/:sessionId" element={<InteractiveExportView />} />
              <Route path="/funnel/:token" element={<FunnelShareView />} />
              <Route path="/auth/begin" element={<AuthHandoffBegin />} />
              <Route path="/auth/handoff" element={<AuthHandoff />} />
              <Route path="/auth/*" element={<CentralAuthRoute><Auth /></CentralAuthRoute>} />
              <Route path="/signin" element={<CentralAuthRoute canonicalPath="/auth"><Auth /></CentralAuthRoute>} />
              <Route path="/login" element={<CentralAuthRoute canonicalPath="/auth"><Auth /></CentralAuthRoute>} />
              <Route path="/sign-in" element={<CentralAuthRoute canonicalPath="/auth"><Auth /></CentralAuthRoute>} />
              <Route path="/log-in" element={<CentralAuthRoute canonicalPath="/auth"><Auth /></CentralAuthRoute>} />
              <Route path="/forgot-password" element={<CentralAuthRoute><ForgotPassword /></CentralAuthRoute>} />
              <Route path="/update-password" element={<CentralAuthRoute><UpdatePassword /></CentralAuthRoute>} />
              <Route path="/eula" element={<Eula />} />
              <Route path="/imprint" element={<Imprint />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/ambassador-program" element={<AmbassadorProgram />} />
              <Route path="/auth/callback" element={<CentralAuthRoute><AuthCallback /></CentralAuthRoute>} />
              <Route path="/confirm-email" element={<CentralAuthRoute><ConfirmEmail /></CentralAuthRoute>} />
              <Route path="/logged-out" element={<CentralAuthRoute><LoggedOut /></CentralAuthRoute>} />
              <Route path="/oauth/consent" element={<CentralAuthRoute><OAuthConsent /></CentralAuthRoute>} />
              <Route path="/oauth/apps/:clientId/permissions" element={<CentralAuthRoute><McpPermissionsWizard /></CentralAuthRoute>} />
              <Route path="/email" element={<MailLandingPage />} />
              <Route path="/mail" element={<ProtectedMailApp />} />
              <Route path={routePatterns.productById} element={<ListingDetail />} />
              <Route path={routePatterns.productBySlug} element={<ListingDetail />} />
              <Route path="/dashboard" element={<LegacyDashboardRedirect />} />
              <Route path="/dashboard/:businessId" element={<DashboardBusinessRedirect />} />
              <Route path={routePatterns.rootWorkspace} element={<Dashboard />} />
              <Route path="/workspace/money/investments" element={<Dashboard />} />
              <Route path="/workspace/finance/investments" element={<Dashboard />} />
              <Route path="/workspace/businesses/:businessId/invest" element={<LazyRoute><BusinessInvestPage /></LazyRoute>} />
              <Route path="/workspace/businesses/:businessId/invest/new" element={<LazyRoute><BusinessInvestPage mode="new" /></LazyRoute>} />
              <Route path="/workspace/businesses/:businessId/invest/:campaignId" element={<LazyRoute><BusinessInvestPage /></LazyRoute>} />
              <Route path="/workspace/businesses/:businessId/invest/:campaignId/edit" element={<LazyRoute><BusinessInvestPage mode="edit" /></LazyRoute>} />
              <Route path="/workspace/admin/investments" element={<LazyRoute><AdminInvestmentsPage /></LazyRoute>} />
              <Route path="/workspace/admin/investments/:campaignId" element={<LazyRoute><AdminInvestmentsPage /></LazyRoute>} />
              <Route path={routePatterns.rootWorkspacePage} element={<Dashboard />} />
              <Route path={routePatterns.rootWorkspaceNestedPage} element={<Dashboard />} />
              <Route path={routePatterns.accountWorkspace} element={<Dashboard />} />
              <Route path={routePatterns.accountWorkspacePage} element={<Dashboard />} />
              <Route path={routePatterns.accountWorkspaceNestedPage} element={<Dashboard />} />
              <Route path="/add/:userId" element={<AddFriendPage />} />
              <Route path="/chat" element={<LegacyChatRedirect />} />
              <Route path="/chat/invite/:token" element={<ChatInvitePage />} />
              <Route path="/business-chat/invite/:token" element={<BusinessChatInvitePage />} />
              <Route path="/bcard" element={<PublicBusinessCard />} />
              <Route path="/bcard/:slug" element={<PublicBusinessCard />} />
              <Route path="/bcard/:slug/details" element={<PublicBusinessCard />} />
              <Route path="/pd/:username" element={<PublicDmPage />} />
              <Route path="/public-dm/:username" element={<PublicDmPage />} />
              <Route path="/selling-tips" element={<SellingTips />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<Blog />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/:id" element={<Checkout />} />
              <Route path={routePatterns.businessPlanCheckout} element={<BusinessPlanCheckout />} />
              <Route path="/ai-checkout/:planId" element={<AiPlanCheckout />} />
              <Route path="/success/:id" element={<Success />} />
              <Route path="/access/:id" element={<AccessContent />} />
              <Route path="/free-confirm/:id" element={<FreeConfirmation />} />
              <Route path="/community" element={<CommunityView />} />
              <Route path="/community/docs" element={<DocumentationView />} />
              <Route path="/community/:communityId/subscribe/:slug" element={<CommunitySubscriptionPayPage />} />
              <Route path="/community/:id" element={<CommunityView />} />
              <Route path="/warranty-rules" element={<WarrantyRules />} />
              <Route path="/live-chat" element={<LiveChat />} />
              <Route path="/help/:slug" element={<HelpArticle />} />
              <Route path="/setup" element={<SetupAccount />} />
              <Route path="/setup-account" element={<SetupAccount />} />
              <Route path="/upload-mobile/:sessionId" element={<MobileUpload />} />
              <Route path="/scan/listing-upload" element={<MobileUpload />} />
              <Route path="/affiliate/join" element={<AffiliateJoinView />} />
              <Route path="/r/:username" element={<CreatorReferralRedirect />} />
              <Route path="/r/:username/:slug" element={<CreatorReferralRedirect />} />
              
              {/* Custom Payment Routes */}
              <Route path="/sandbox/pay/invoice/:username/:invoiceId" element={<InvoicePaymentPage />} />
              <Route path="/sandbox/:username/quick-pay/:slug" element={<QuickPayCheckout />} />
              <Route path="/s/:username/quick-pay/:slug" element={<QuickPayCheckout />} />
              <Route path="/payout/setup/:token" element={<PayoutRecipientSetup />} />
              <Route path="/pay/invoice/:username/:invoiceId" element={<InvoicePaymentPage />} />
              <Route path="/wersee-pay-security" element={<WerseePaySecurityPage />} />
              <Route path="/account-security" element={<AccountSecurityPage />} />
              <Route path="/invoice/view/:invoiceId" element={<InvoicePdfViewer />} />
              <Route path="/quick-pay/invoice/:invoiceNumber" element={<OldInvoicePaymentPage />} />
              <Route path="/quick-pay/:username/:slug" element={<QuickPayCheckout />} />
              <Route path="/:username/quick-pay/invoice/:slug" element={<OldInvoicePaymentPage />} />
              <Route path="/:username/quick-pay/:slug" element={<QuickPayCheckout />} />
              <Route path="/:username/invoice/:slug" element={<InvoicePublicView />} />
              <Route path="/:username/proposal/:id" element={<ProposalPublicView />} />
              <Route path="/proposal/:id" element={<ProposalPublicView />} />
              <Route path="/contract/:id" element={<ContractPublicView />} />
              <Route path="/:username/subscriptions/:slug" element={<SubscriptionPublicView />} />
              <Route path={routePatterns.accountPosTerminal} element={<PosTerminal />} />
              <Route path={routePatterns.accountPosNfc} element={<PosNfcMobile />} />
              <Route path={routePatterns.accountPosCheckout} element={<PosCheckoutPage />} />
              <Route path={routePatterns.accountQrPay} element={<PosCheckoutPage />} />
              <Route path="/auth/:systemname/:token" element={<PosAuth />} />
              
              <Route path="/features/next-gen-pos" element={<NextGenPos />} />
              <Route path="/features/wersee-pay" element={<WerseePay />} />
              <Route path="/features/command-center" element={<CommandCenter />} />
              <Route path="/features/omni-management" element={<OmniManagement />} />
              <Route path="/features/academy-builder" element={<AcademyBuilder />} />
              <Route path="/features/partner-network" element={<PartnerNetwork />} />
              <Route path="/features/zero-cost-entry" element={<ZeroCostEntry />} />
              <Route path="/features/venture-launchpad" element={<VentureLaunchpad />} />
              <Route path="/features/intelligence-core" element={<IntelligenceCore />} />
              <Route path="/creators" element={<CreatorMode />} />
              <Route path="/creators/onboarding" element={<CreatorOnboardingPage />} />
              <Route path="/creators/dashboard" element={<CreatorOverview />} />
              <Route path="/creators/profile" element={<CreatorProfile />} />
              <Route path="/creators/analytics" element={<CreatorAnalytics />} />
              <Route path="/creators/links" element={<CreatorLinks />} />
              <Route path="/creators/campaigns" element={<CreatorCampaigns />} />
              <Route path="/creators/audience" element={<CreatorAudience />} />
              <Route path="/creators/revenue" element={<CreatorRevenue />} />
              <Route path="/creators/earnings" element={<CreatorEarnings />} />
              <Route path="/creators/payouts" element={<CreatorPayouts />} />
              <Route path="/creators/invites" element={<CreatorInvites />} />
              <Route path="/creators/invite/:username" element={<CreatorInvites />} />
              <Route path="/creators/platforms" element={<CreatorPlatforms />} />
              <Route path="/creators/share-kit" element={<CreatorShareKit />} />
              <Route path="/creators/docs" element={<CreatorDocs />} />
              <Route path="/creators/settings" element={<CreatorSettings />} />
              <Route path="/creator/:username" element={<PublicCreatorProfile />} />
              <Route path="/treasure" element={<WerseeTreasure />} />
              <Route path="/invoices" element={<WerseeInvoices />} />
              <Route path="/info/buddy" element={<WerseeBuddyLanding />} />
              <Route path="/buddy" element={<Navigate to="/info/buddy" replace />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/roadmap" element={<Roadmap />} />
              <Route path="/next-gen" element={<NextGenLanding />} />
              <Route path="/next-gen-setup" element={<NextGenSetup />} />
              <Route path="/next-gen-invite/:token" element={<NextGenInvite />} />
              <Route path="/edu-games" element={<EduGames />} />
              <Route path="/guardian-portal" element={<GuardianPortal />} />
              <Route path="/safety-controls" element={<SafetyControls />} />
              <Route path="/pages" element={<AllPages />} />
              <Route path="/verify" element={<CentralAuthRoute><SecurityVerify /></CentralAuthRoute>} />
              <Route path="/sovereign" element={<WerseeSovereign />} />
              <Route path="/transparency" element={<Transparency />} />
              <Route path="/download" element={<DownloadApp />} />
              <Route path="/bot-guide" element={<BotGuide />} />
              <Route path="/auth/link" element={<CentralAuthRoute><LinkAccount /></CentralAuthRoute>} />
              <Route path="/passkeys" element={<Navigate to="/workspace/passkeys" replace />} />
              <Route path="/settings/passkeys" element={<Navigate to="/workspace/settings/passkeys" replace />} />
              <Route path="/account/passkeys" element={<Navigate to="/workspace/settings/passkeys" replace />} />
              <Route path="/security/passkeys" element={<Navigate to="/workspace/settings/passkeys" replace />} />
              <Route path="/settings/security" element={<Navigate to="/workspace/settings/security" replace />} />
              <Route path="/settings/security/*" element={<Navigate to="/workspace/settings/security" replace />} />
              <Route path="/account/security" element={<Navigate to="/workspace/settings/security" replace />} />
              <Route path="/account/security/*" element={<Navigate to="/workspace/settings/security" replace />} />
              <Route path="/security" element={<Navigate to="/workspace/settings/security" replace />} />
              <Route path="/security/sessions" element={<Navigate to="/workspace/settings/security" replace />} />
              <Route path="/security/sessions/*" element={<Navigate to="/workspace/settings/security" replace />} />
              <Route path="/settings" element={<Navigate to="/workspace/settings" replace />} />
              <Route path="/account/settings" element={<Navigate to="/workspace/settings" replace />} />
              <Route path="/settings/:section" element={<SettingsRedirect />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/jobs/:productId" element={<ListingDetail />} />
              <Route path="/jobs/:id/apply" element={<JobApplication />} />
              <Route path="/portal/:businessSlug" element={<BusinessPortal />} />
              <Route path={routePatterns.accountInvest} element={<LazyRoute><InvestMarketplace /></LazyRoute>} />
              <Route path={routePatterns.accountInvestStock} element={<LazyRoute><MarketAssetDetail /></LazyRoute>} />
              <Route path={routePatterns.accountInvestEtf} element={<LazyRoute><MarketAssetDetail /></LazyRoute>} />
              <Route path={routePatterns.accountInvestCrypto} element={<LazyRoute><MarketAssetDetail /></LazyRoute>} />
              <Route path={routePatterns.accountInvestWersee} element={<LazyRoute><WerseeListingDetail /></LazyRoute>} />
              <Route path={routePatterns.accountInvestCampaign} element={<LazyRoute><InvestCampaignDetail /></LazyRoute>} />
              <Route path={routePatterns.accountInvestCheckout} element={<LazyRoute><InvestCheckout /></LazyRoute>} />
              <Route path={routePatterns.publicFundCampaign} element={<LazyRoute><InvestCampaignDetail /></LazyRoute>} />
              <Route path={routePatterns.publicFundCheckout} element={<LazyRoute><InvestCheckout /></LazyRoute>} />
              <Route path="/invest/mobile/:investmentToken" element={<LazyRoute><InvestMarketplace /></LazyRoute>} />
              <Route path="/invest/asset/:investmentName" element={<LazyRoute><InvestMarketplace /></LazyRoute>} />
              <Route path="/invest/markets" element={<LazyRoute><InvestMarketplace view="markets" /></LazyRoute>} />
              <Route path="/invest/markets/:category" element={<LazyRoute><InvestMarketplace view="markets" /></LazyRoute>} />
              <Route path="/invest/watchlist" element={<LazyRoute><InvestMarketplace view="watchlist" /></LazyRoute>} />
              <Route path="/invest/learn" element={<LazyRoute><InvestMarketplace view="learn" /></LazyRoute>} />
              <Route path="/invest" element={<LazyRoute><InvestMarketplace /></LazyRoute>} />
              <Route path="/invest/stocks/:slug" element={<LazyRoute><MarketAssetDetail /></LazyRoute>} />
              <Route path="/invest/etfs/:slug" element={<LazyRoute><MarketAssetDetail /></LazyRoute>} />
              <Route path="/invest/crypto/:slug" element={<LazyRoute><MarketAssetDetail /></LazyRoute>} />
              <Route path="/invest/wersee/:slug" element={<LazyRoute><WerseeListingDetail /></LazyRoute>} />
              <Route path="/invest/:campaignSlug" element={<LazyRoute><InvestCampaignDetail /></LazyRoute>} />
              <Route path="/invest/:campaignSlug/checkout" element={<LazyRoute><InvestCheckout /></LazyRoute>} />
              <Route path="/investments" element={<Navigate to="/invest" replace />} />
              <Route path="/investments/:id" element={<InvestmentDetails />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/features/flash-checkout" element={<FlashCheckout />} />
              <Route path="/demo/flash-checkout" element={<DemoFlashCheckout />} />
              
              {/* Education & School Routes */}
              <Route path="/school" element={<SchoolLandingPage />} />
              <Route path="/for-school" element={<Navigate to="/school" replace />} />
              <Route path="/edu/school" element={<Navigate to="/school" replace />} />
              <Route path="/school/join" element={<SchoolJoinPage />} />
              <Route path="/school/join/:code" element={<SchoolJoinPage />} />
              <Route path="/edu/student" element={<StudentDiscount />} />
              <Route path="/edu/campus" element={<CampusProgram />} />
              <Route path="/edu/paths" element={<LearningPaths />} />
              <Route path="/edu/resources" element={<EduResources />} />
              
              <Route path="/invite/:role/:token" element={<InvitePage />} />
              <Route path="/invite/:token" element={<InvitePage />} />
              <Route path="/join/c/:communityId/:communityName" element={<CommunityInvitePage />} />
              <Route path="/join/i/:inviteCode" element={<CommunityInvitePage />} />
              <Route path="/c/:customUrl" element={<CommunityInvitePage />} />
              <Route path="/community/:communityId/m/:messageId" element={<MessageSharePage />} />
              <Route path="/dashboard/ads" element={<AdsManager />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/package/confirm/:publicCode/:token" element={<LazyRoute><PackageConfirmation /></LazyRoute>} />
              <Route path="/payout-info" element={<PayoutInfoPage />} />
              <Route path="/status" element={<StatusPage />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/admin/plans" element={<AdminPlans />} />
              <Route path="/app/:appId" element={<CustomAppLP />} />
              <Route path="/custom-app-build" element={<CustomAppBuild />} />
              <Route path="/enterprise" element={<EnterpriseSolutions />} />
              <Route path="/book/:configId" element={<CallBooking />} />
              <Route path="/:username/book/:configSlug" element={<CallBooking />} />
              <Route path="/:businessSlug/book/:configSlug" element={<CallBooking />} />
              <Route path="/management/bookings" element={<BookingDashboard />} />
              <Route path="/management/create-call" element={<CallConfigWizard />} />
              <Route path="/earn" element={<CityCampaign />} />
              <Route path="/g/:id" element={<GiveawayLandingPage />} />
              <Route path="/f/:slug" element={<FormPublicView />} />
              <Route path="/builder/:type" element={<BuilderPage />} />
              <Route path="/builder/:type/:id" element={<BuilderPage />} />
              <Route path="/create" element={<Create />} />
              <Route path="/create/:type" element={<div className="min-h-screen bg-[var(--store-bg)]" />} />
              <Route path="/create/:type/:id" element={<div className="min-h-screen bg-[var(--store-bg)]" />} />
              <Route path="/welcome/:source" element={<SocialLandingPage />} />
              {/* Redirect aliases for broken/linked pages */}
              <Route path="/ambassador" element={<Navigate to="/ambassador-program" replace />} />
              <Route path="/cookie-policy" element={<Navigate to="/cookies" replace />} />
              <Route path="/partners" element={<PlaceholderPage title="Partner Program" />} />
              <Route path="/portal/demo" element={<Navigate to="/demo/flash-checkout" replace />} />

              <Route path={routePatterns.rootEntity} element={<ProfileOrBusiness />} />
              <Route path={routePatterns.notFound} element={<NotFound />} />
            </Routes></motion.div>
            {storeCheckoutBackground && (
              <Routes location={location}>
                <Route path="/checkout/:id" element={<Checkout presentation="sheet" />} />
              </Routes>
            )}
            </React.Suspense>
          </AnimatePresence>
        </RouteErrorBoundary>
        {!hideNavAndFooter && !useStoreNavigation && <Footer />}
      </main>

      {!hideNavAndFooter && !useStoreNavigation && <SetupBanner />}
      <CookieBanner />
    </div>
  );
};

import { ListingWizardProvider, useListingWizard } from './context/ListingWizardContext';

import { VerificationBanner } from './components/VerificationBanner';
import { Toaster } from 'sonner';
import { FeedbackProvider } from './lib/feedback';

import { SecurityAudit } from './components/SecurityAudit';
import { MobileOptimization } from './components/MobileOptimization';
import { MobileKeyboardPill } from './components/MobileKeyboardPill';
import { getLocaleFromPathname } from './lib/locales';
import { WerseeAiProvider } from './ai/context';

const ListingWizard = lazyNamed(() => import('./components/listings/ListingWizard'), 'ListingWizard');

const DeferredListingWizard = () => {
  const { isOpen, closeWizard } = useListingWizard();
  const location = useLocation();
  const { user, loading } = useAuth();
  React.useEffect(() => { if (location.pathname === '/create' && isOpen) closeWizard(); }, [location.pathname, isOpen, closeWizard]);
  if (location.pathname === '/create') return null;
  if (!isOpen && !location.pathname.startsWith('/create/')) return null;
  if (loading) return null;
  if (!user) return <Navigate replace to={`/signin?returnUrl=${encodeURIComponent(location.pathname + location.search)}`} />;
  return <React.Suspense fallback={null}><ListingWizard /></React.Suspense>;
};

const isMailHostname = () => {
  const hostname = window.location.hostname.toLowerCase();
  return hostname === 'email.wersee.com'
    || hostname === 'mail.wersee.com'
    || hostname === 'email.wersee.localhost'
    || /^wersee-mail-bridge(?:-[a-z0-9-]+)?\.vercel\.app$/.test(hostname);
};

const isBlogHostname = () => {
  const hostname = window.location.hostname.toLowerCase();
  return hostname === 'blog.wersee.com' || hostname === 'blog.wersee.localhost';
};

const MainApplicationSurface = () => (
  <>
    <SecurityAudit />
    <MobileOptimization />
    <GlobalProtection />
    <BrowserRouter basename={getLocaleFromPathname(window.location.pathname) ? `/${getLocaleFromPathname(window.location.pathname)}` : undefined}>
      <LocaleProvider>
        <FeedbackProvider>
          <Toaster position="top-center" richColors />
          <MobileKeyboardPill />
          <VerificationBanner />
          <AnimatedRoutes />
          <DeferredListingWizard />
        </FeedbackProvider>
      </LocaleProvider>
    </BrowserRouter>
  </>
);

const LazyInvestApplicationSurface = React.lazy(() => import('./pages/invest/InvestApplicationSurface'));
const InvestApplicationSurface = () => <React.Suspense fallback={<div className="min-h-screen p-12" role="status">Loading Wersee Invest…</div>}><LazyInvestApplicationSurface /></React.Suspense>;

const MailApplicationSurface = () => (
  <BrowserRouter>
    <Toaster position="top-center" richColors />
    <React.Suspense fallback={<div className="min-h-[100dvh] bg-[#08080a]" />}>
      <MailSubdomainRoutes />
    </React.Suspense>
  </BrowserRouter>
);

const BlogApplicationSurface = () => (
  <BrowserRouter>
    <Toaster position="top-center" richColors />
    <React.Suspense fallback={<div className="min-h-[100dvh] bg-[#f5f5f2]" />}>
      <Routes>
        <Route path="*" element={<Blog />} />
      </Routes>
    </React.Suspense>
  </BrowserRouter>
);

const IdentityLoadingScreen = () => (
  <div
    role="status"
    aria-live="polite"
    className="grid min-h-[100dvh] place-items-center bg-[#f1f1f7] px-6 text-center text-[#17171a] dark:bg-[#060608] dark:text-white"
  >
    <main className="w-full max-w-sm rounded-[28px] border border-black/[0.06] bg-white p-8 shadow-[0_28px_90px_rgba(59,46,117,0.14)] dark:border-white/10 dark:bg-white/[0.055] dark:shadow-[0_30px_100px_rgba(0,0,0,0.42)]">
      <span className="mx-auto grid h-14 w-14 place-items-center rounded-[18px] bg-[#17171a] text-lg font-black text-white dark:bg-white dark:text-black">W</span>
      <h1 className="mt-6 text-xl font-black tracking-tight">Opening Wersee ID</h1>
      <p className="mt-2 text-sm leading-6 text-black/45 dark:text-white/45">Securing your sign-in and return to the correct Wersee service.</p>
      <div className="mx-auto mt-6 h-5 w-5 animate-spin rounded-full border-2 border-black/15 border-t-black dark:border-white/20 dark:border-t-white" aria-hidden="true" />
    </main>
  </div>
);

class IdentitySurfaceErrorBoundary extends React.Component<React.PropsWithChildren, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Wersee ID surface failed to render:', error, errorInfo);
  }

  retry = () => this.setState({ failed: false });

  render() {
    if (!this.state.failed) return this.props.children;
    const login = new URL('/login', window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);
    const returnUrl = currentParams.get('returnUrl') || currentParams.get('redirect');
    if (returnUrl) login.searchParams.set('returnUrl', returnUrl);

    return (
      <div role="alert" className="grid min-h-[100dvh] place-items-center bg-[#f1f1f7] px-6 text-center text-[#17171a] dark:bg-[#060608] dark:text-white">
        <main className="w-full max-w-sm rounded-[28px] border border-black/[0.06] bg-white p-8 shadow-[0_28px_90px_rgba(59,46,117,0.14)] dark:border-white/10 dark:bg-white/[0.055] dark:shadow-[0_30px_100px_rgba(0,0,0,0.42)]">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-[18px] bg-[#17171a] text-lg font-black text-white dark:bg-white dark:text-black">W</span>
          <h1 className="mt-6 text-xl font-black tracking-tight">Wersee ID could not open</h1>
          <p className="mt-2 text-sm leading-6 text-black/45 dark:text-white/45">Your sign-in was not lost. Try the secure screen again or restart sign-in.</p>
          <div className="mt-6 grid gap-3">
            <button type="button" onClick={this.retry} className="rounded-full bg-[#17171a] px-5 py-3 text-sm font-bold text-white dark:bg-white dark:text-black">Try again</button>
            <a href={login.toString()} className="rounded-full border border-black/10 bg-black/[0.035] px-5 py-3 text-sm font-semibold text-[#17171a] dark:border-white/10 dark:bg-white/5 dark:text-white">Restart sign-in</a>
          </div>
        </main>
      </div>
    );
  }
}

const IdentitySurfaceBoundary = ({ children }: React.PropsWithChildren) => {
  const location = useLocation();
  return <IdentitySurfaceErrorBoundary key={`${location.pathname}${location.search}`}>{children}</IdentitySurfaceErrorBoundary>;
};

const AuthApplicationSurface = () => (
  <BrowserRouter>
    <Toaster position="top-center" richColors />
    <React.Suspense fallback={<div className="min-h-[100dvh] bg-[#050505]" />}>
      <Routes>
        <Route path="/" element={<Navigate to="/account" replace />} />
        <Route path="/account/*" element={<IdentityCenter />} />
        <Route path="/profile" element={<Navigate to="/account/profile" replace />} />
        <Route path="/security" element={<Navigate to="/account/security" replace />} />
        <Route path="/auth/begin" element={<ReturnToWerseeApp />} />
        <Route path="/auth/handoff" element={<ReturnToWerseeApp />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/link" element={<LinkAccount />} />
        <Route path="/auth/:systemname/:token" element={<PosAuth />} />
        <Route path="/auth/*" element={<Auth />} />
        <Route path="/signin" element={<Navigate to="/auth" replace />} />
        <Route path="/login" element={<Navigate to="/auth" replace />} />
        <Route path="/sign-in" element={<Navigate to="/auth" replace />} />
        <Route path="/log-in" element={<Navigate to="/auth" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />
        <Route path="/confirm-email" element={<ConfirmEmail />} />
        <Route path="/logged-out" element={<LoggedOut />} />
        <Route path="/verify" element={<SecurityVerify />} />
        <Route path="/oauth/consent" element={<OAuthConsent />} />
        <Route path="/oauth/apps/:clientId/permissions" element={<McpPermissionsWizard />} />
        <Route path="*" element={<ReturnToWerseeApp />} />
      </Routes>
    </React.Suspense>
  </BrowserRouter>
);

const IdentityApplicationSurface = () => (
  <BrowserRouter>
    <Toaster position="top-center" richColors />
    <IdentitySurfaceBoundary>
      <React.Suspense fallback={<IdentityLoadingScreen />}>
        <Routes>
          <Route path="/" element={<Navigate to="/account/overview" replace />} />
          <Route path="/account/*" element={<WerseeIdCenter />} />
          <Route path="/profile" element={<Navigate to="/account/profile" replace />} />
          <Route path="/security" element={<Navigate to="/account/security" replace />} />
          <Route path="/security/sessions" element={<Navigate to="/account/sessions" replace />} />
          <Route path="/security/passkeys" element={<Navigate to="/account/passkeys" replace />} />
          <Route path="/login" element={<WerseeIdAuth />} />
          <Route path="/signup" element={<WerseeIdAuth />} />
          <Route path="/forgot-password" element={<WerseeIdAuth />} />
          <Route path="/verify-email" element={<WerseeIdAuth />} />
          <Route path="/reset-password" element={<WerseeIdAuth />} />
          <Route path="/mfa" element={<WerseeIdAuth />} />
          <Route path="/error" element={<WerseeIdAuth />} />
          <Route path="/auth" element={<WerseeIdAuth />} />
          <Route path="/signin" element={<WerseeIdAuth />} />
          <Route path="/sign-in" element={<WerseeIdAuth />} />
          <Route path="/log-in" element={<WerseeIdAuth />} />
          <Route path="/oauth/consent" element={<WerseeIdOAuthConsent />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </React.Suspense>
    </IdentitySurfaceBoundary>
  </BrowserRouter>
);

export default function App() {
  if (isIdentityHostname(window.location.hostname)) {
    return (
      <ThemeProvider>
        <IdentityApplicationSurface />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <WerseeAiProvider>
        <HelmetProvider>
          <ListingWizardProvider>
            {isAuthHostname(window.location.hostname)
              ? <AuthApplicationSurface />
              : isMailHostname()
              ? <MailApplicationSurface />
              : isBlogHostname()
                  ? <BlogApplicationSurface />
                : isInvestHostname() ? <InvestApplicationSurface /> : <MainApplicationSurface />}
          </ListingWizardProvider>
        </HelmetProvider>
        </WerseeAiProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
